/**
 * 「昨夜の整理」のカード（ADR-0025 の 8。本人の決定 2026-10-07: 最初は PWA のカード、ボタンで答える）。
 * 会話の上に出す。答えはボタンだけで受ける（会話の「はい」では受けない）。押したら実際に何が変わるかを併記する（Gateway がコードで作る文）。
 * 健康の候補・健康の要約は畳み、開いたときだけ中身を出す。
 */
import { ApiError, api } from "./api.js";

interface PendingFact {
  id: string;
  subject: string;
  predicate: string;
  object: string;
  domain: "general" | "health";
  sources: number;
}
interface PendingCandidate {
  id: string;
  type: "duplicate" | "conflict";
  health: boolean;
  default_keep: string;
  facts: PendingFact[];
  effects: { merge?: string; choose?: Record<string, string>; separate: string };
}
interface AppliedCandidate {
  id: string;
  type: "duplicate" | "conflict";
  action: "merge" | "choose";
  health: boolean;
  revert_effect: string;
}
interface L1View {
  id: string;
  period_start: string;
  domain: "general" | "health";
  items: { text: string; flags: string[]; sources: number }[];
  excluded: Record<string, number>;
}
interface PendingTunable {
  id: string;
  kind: "threshold" | "config" | "rollback";
  tunable_id: string;
  label: string;
  text: string;
  current: number;
  proposed: number;
  warn: boolean;
  effect: string;
}
interface AppliedTunable {
  id: string;
  tunable_id: string;
  label: string;
  from: number;
  to: number;
  effect: string;
  nights: number;
  worse_streak: number;
}
export interface NightlyView {
  enabled: boolean;
  last_run: { run_date: string; status: string; batch_state: string } | null;
  missing: boolean;
  candidates: PendingCandidate[];
  hidden_health: number;
  stale_pending: number;
  applied: AppliedCandidate[];
  l1: L1View[];
  hidden_health_l1: number;
  /** 調整の提案（ADR-0047）と、反映した調整（戻せる） */
  tunables?: PendingTunable[];
  tunables_applied?: AppliedTunable[];
  /** 昨夜の振り返り（ADR-0036）の出入りの数。回収されていなければ無い */
  self?: {
    status: string;
    added: number;
    revised: number;
    retired: number;
    journal: number;
    wishes: number;
    held: number;
    churn_warn: boolean;
  };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  e.append(...children);
  return e;
}

const factLabel = (f: PendingFact) => `${f.subject} / ${f.predicate}: ${f.object}`;

/** 健康のものは畳む（開いたときだけ中身を出す） */
function fold(health: boolean, summary: string, body: HTMLElement): HTMLElement {
  if (!health) return body;
  return el("details", { class: "health" }, el("summary", {}, summary), body);
}

/** カードを描く。何も無ければ空にする。答えたら描き直す */
export async function renderNightlyCard(root: HTMLElement): Promise<void> {
  let view: NightlyView;
  try {
    view = await api<NightlyView>("/v1/nightly/pending");
  } catch (e) {
    // 表や権限が未適用（500）・機能が無い（404）・声の端末（403）なら、カードを出さない
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    return;
  }
  const notes: string[] = [];
  if (view.missing) notes.push("昨夜の整理は動いていません（記録がありません）。");
  else if (view.last_run?.status === "error") notes.push("昨夜の整理は失敗しました。");
  else if (view.last_run?.status === "partial") notes.push("昨夜の整理は一部だけ終わりました。");
  if (view.last_run?.batch_state === "submitted") notes.push("昨日の要約はまだ届いていません。");
  if (view.stale_pending > 0)
    notes.push(`2 週間以上そのままの確認が ${view.stale_pending} 件あります。`);
  if (view.hidden_health + view.hidden_health_l1 > 0)
    notes.push(
      `健康に関するものが ${view.hidden_health + view.hidden_health_l1} 件あります（設定で非表示）。`,
    );
  // 昨夜の振り返り（ADR-0036 の決定 5）。保留があれば目立たせる（中身は「Jarvis のメモ」の画面で）
  const s = view.self;
  const extra = s
    ? [
        ...(s.journal ? [`自伝 ${s.journal} 件`] : []),
        ...(s.wishes ? [`やりたいこと ${s.wishes} 件`] : []),
        `保留 ${s.held} 件`,
      ].join("・")
    : "";
  const selfLine = s
    ? s.status === "collected"
      ? `昨夜、Jarvis がメモを ${s.added} 件足し ${s.revised} 件直し ${s.retired} 件消しました（${extra}）。`
      : `昨夜の振り返りは受け取れませんでした（${s.status}）。`
    : null;

  const tunables = view.tunables ?? [];
  const tunablesApplied = view.tunables_applied ?? [];
  const empty =
    view.candidates.length === 0 &&
    view.applied.length === 0 &&
    view.l1.length === 0 &&
    tunables.length === 0 &&
    tunablesApplied.length === 0 &&
    !selfLine;
  if (!view.enabled && empty) {
    root.replaceChildren();
    return;
  }
  if (empty && notes.length === 0) {
    root.replaceChildren();
    return;
  }

  const status = el("p", { class: "muted small" });
  const answer = async (body: Record<string, unknown>, btn: HTMLButtonElement) => {
    btn.disabled = true;
    status.textContent = "反映しています…";
    try {
      await api("/v1/nightly/answer", body);
      await renderNightlyCard(root);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e);
      const msg: Record<string, string> = {
        stale: "事実が変わっていたため、この確認は取り下げました。",
        already_answered: "もう答えてあります。",
        conflict: "今は戻せません（同じ事柄を後から覚え直しています）。",
        cannot_revert: "これは戻せません。",
        rules_unavailable: "調整の決まりがまだ入っていないため、反映できません。",
        out_of_range: "範囲の外の値なので反映しません。",
        protected: "保護対象の設定なので反映しません。",
      };
      status.textContent = msg[code] ?? `反映できませんでした（${code}）`;
      btn.disabled = false;
    }
  };
  const button = (text: string, body: Record<string, unknown>, cls = "") => {
    const b = el("button", cls ? { class: cls } : {}, text) as HTMLButtonElement;
    b.onclick = () => void answer(body, b);
    return b;
  };

  const sections: HTMLElement[] = [];
  for (const c of view.candidates) {
    const [a, b] = c.facts as [PendingFact, PendingFact];
    const body = el(
      "div",
      { class: "nightly-item" },
      el("p", {}, c.type === "duplicate" ? "同じ事柄かもしれません" : "値が食い違っています"),
      el("ul", {}, ...c.facts.map((f) => el("li", {}, `${factLabel(f)}（出典 ${f.sources}）`))),
    );
    const row = el("div", { class: "row wrap" });
    const effects = el("ul", { class: "effects small muted" });
    if (c.type === "duplicate" && c.effects.merge) {
      row.append(
        button(
          "まとめる",
          { action: "merge", candidate_id: c.id, keep_fact_id: c.default_keep },
          "primary",
        ),
      );
      effects.append(el("li", {}, `まとめる: ${c.effects.merge}`));
    } else {
      for (const f of [a, b]) {
        row.append(
          button(`「${f.object}」が正しい`, {
            action: "choose",
            candidate_id: c.id,
            keep_fact_id: f.id,
          }),
        );
        const eff = c.effects.choose?.[f.id];
        if (eff) effects.append(el("li", {}, `「${f.object}」が正しい: ${eff}`));
      }
    }
    row.append(button("そのまま（別の事柄）", { action: "separate", candidate_id: c.id }));
    effects.append(el("li", {}, `そのまま: ${c.effects.separate}`));
    body.append(row, effects);
    sections.push(fold(c.health, "健康に関する確認（開くと表示）", body));
  }
  for (const c of view.applied) {
    const body = el(
      "div",
      { class: "nightly-item" },
      el("p", { class: "small" }, c.action === "merge" ? "最近まとめた事実" : "最近選んだ事実"),
      el("p", { class: "small muted" }, `戻す: ${c.revert_effect}`),
      el("div", { class: "row" }, button("戻す", { action: "revert", candidate_id: c.id })),
    );
    sections.push(fold(c.health, "健康に関するもの（開くと表示）", body));
  }
  // 調整の提案（ADR-0047）。はい / いいえ / あとで。押したら実際に変わる設定の場所と値を併記する
  for (const t of tunables) {
    const body = el(
      "div",
      { class: "nightly-item" },
      el(
        "p",
        {},
        t.kind === "rollback" ? "調整を戻す提案" : "調整の提案",
        t.warn ? el("span", { class: "flag" }, "（確認を減らす・広げる向き）") : "",
      ),
      el("p", {}, t.text),
      el(
        "div",
        { class: "row wrap" },
        button("はい", { action: "tunable", candidate_id: t.id, decision: "yes" }, "primary"),
        button("いいえ", { action: "tunable", candidate_id: t.id, decision: "no" }),
        button("あとで", { action: "tunable", candidate_id: t.id, decision: "later" }),
      ),
      el(
        "ul",
        { class: "effects small muted" },
        el("li", {}, `はい: ${t.effect}`),
        el("li", {}, "いいえ: 何も変えません。同じ向きの提案はしばらく出しません。"),
        el("li", {}, "あとで: 何も変えません。数日たったらまた出します。"),
      ),
    );
    sections.push(body);
  }
  for (const t of tunablesApplied) {
    sections.push(
      el(
        "div",
        { class: "nightly-item" },
        el("p", { class: "small" }, `最近変えた調整: ${t.label} ${t.from} → ${t.to}`),
        el(
          "p",
          { class: t.worse_streak > 0 ? "small flag" : "small muted" },
          `変えてから ${t.nights} 晩見ています` +
            (t.worse_streak > 0 ? `（悪化が ${t.worse_streak} 晩続いています）` : ""),
        ),
        el("p", { class: "small muted" }, `戻す: ${t.effect}`),
        el(
          "div",
          { class: "row" },
          button("戻す", { action: "tunable_revert", candidate_id: t.id }),
        ),
      ),
    );
  }
  for (const l of view.l1) {
    const ex = Object.entries(l.excluded)
      .filter(([, n]) => n > 0)
      .map(
        ([k, n]) =>
          `${({ health: "健康", voice: "声", private: "秘密" } as Record<string, string>)[k] ?? k} ${n}`,
      )
      .join("・");
    const body = el(
      "div",
      { class: "nightly-item" },
      el("p", {}, `${l.period_start} の要約`),
      el(
        "ul",
        {},
        ...l.items.map((i) =>
          el(
            "li",
            {},
            i.text,
            i.flags.includes("number_not_in_source")
              ? el("span", { class: "flag" }, "（数が元の発言に無い）")
              : "",
          ),
        ),
      ),
      ex ? el("p", { class: "small muted" }, `入れなかった会話: ${ex}`) : "",
    );
    const bad = el("button", {}, "違う") as HTMLButtonElement;
    bad.onclick = () => {
      const detail = window.prompt("どこが違いますか（空でも可）") ?? undefined;
      if (detail === undefined) return;
      void answer(
        { action: "l1_rating", l1_id: l.id, rating: "bad", ...(detail ? { detail } : {}) },
        bad,
      );
    };
    body.append(
      el(
        "div",
        { class: "row" },
        button("よい", { action: "l1_rating", l1_id: l.id, rating: "good" }),
        bad,
      ),
    );
    sections.push(
      fold(l.domain === "health", `${l.period_start} の要約（健康の話題を含む。開くと表示）`, body),
    );
  }

  root.replaceChildren(
    el(
      "section",
      { class: "card nightly" },
      el("h2", {}, "昨夜の整理"),
      ...notes.map((n) => el("p", { class: "small muted" }, n)),
      ...(selfLine
        ? [
            el(
              "p",
              { class: s && (s.held > 0 || s.churn_warn) ? "small flag" : "small" },
              selfLine,
              s?.churn_warn ? "（メモの入れ替わりが大きい晩でした）" : "",
            ),
          ]
        : []),
      ...sections,
      status,
    ),
  );
}
